import { make } from "./dom";
import { icon } from "./icons";

export interface Panel {
  element: HTMLElement;
  body: HTMLElement;
  open: () => void;
  close: () => void;
  onClose: (() => void) | null;
}

/** Keep focus and interaction ownership together, including a capture above history. */
export function panelManager(layer: HTMLElement, workspace: HTMLElement) {
  const stack: Panel[] = [];
  const interactiveSurfaces = new Map<Panel, HTMLElement>();
  const previousInert = new Map<HTMLElement, boolean>();
  const focusable = (root: HTMLElement): HTMLElement[] =>
    Array.from(
      root.querySelectorAll<HTMLElement>(
        "button, a[href], input, select, textarea, summary, [tabindex='0']",
      ),
    ).filter(
      (node) =>
        !node.closest("[hidden], [inert]") &&
        !node.matches(":disabled") &&
        node.getClientRects().length > 0,
    );
  const update = (): void => {
    for (const [branch, inert] of previousInert) branch.inert = inert;
    previousInert.clear();
    const top = stack.at(-1);
    const interactive = top ? interactiveSurfaces.get(top) : undefined;
    workspace.inert = stack.length > 0 && !interactive;
    if (interactive) {
      // A setup drawer leaves only its camera usable; session actions remain outside
      // its focus and interaction scope even though they share the same workspace.
      let branch = interactive;
      while (branch !== workspace) {
        const parent = branch.parentElement;
        if (!parent) break;
        for (const sibling of parent.children) {
          if (sibling === branch || !(sibling instanceof HTMLElement)) continue;
          previousInert.set(sibling, sibling.inert);
          sibling.inert = true;
        }
        branch = parent;
      }
    }
    stack.forEach((panel, index) => {
      panel.element.inert = index !== stack.length - 1;
      panel.element.style.zIndex = String(index + 1);
    });
  };
  document.addEventListener("keydown", (event) => {
    const top = stack.at(-1);
    if (!top) return;
    if (event.key === "Escape") {
      event.preventDefault();
      top.close();
    } else if (event.key === "Tab") {
      const interactive = interactiveSurfaces.get(top);
      const nodes = [...(interactive ? focusable(interactive) : []), ...focusable(top.element)];
      const first = nodes[0];
      const last = nodes.at(-1);
      if (!first) {
        event.preventDefault();
        top.element.focus();
      } else if (
        event.shiftKey &&
        (document.activeElement === first || document.activeElement === top.element)
      ) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  });
  return (
    id: string,
    title: string,
    subtitle: string,
    className = "",
    interactiveSurface?: HTMLElement,
  ): Panel => {
    const surface = make("section", `panel ${className}`);
    surface.id = id;
    surface.hidden = true;
    surface.tabIndex = -1;
    surface.setAttribute("role", "dialog");
    const interactive = interactiveSurface && workspace.contains(interactiveSurface);
    surface.setAttribute("aria-modal", interactive ? "false" : "true");
    surface.setAttribute("aria-labelledby", `${id}Title`);
    const header = make("header", "panel-header");
    const heading = make("div");
    if (subtitle) heading.append(make("p", "eyebrow", subtitle));
    const name = make("h2", "", title);
    name.id = `${id}Title`;
    heading.append(name);
    const close = make("button", "icon-button");
    close.append(icon("close"));
    close.type = "button";
    close.setAttribute("aria-label", `Close ${title.toLowerCase()}`);
    header.append(heading, close);
    const body = make("div", "panel-body");
    surface.append(header, body);
    layer.append(surface);
    let previous: HTMLElement | null = null;
    const panel: Panel = {
      element: surface,
      body,
      onClose: null,
      open: () => {
        if (!surface.hidden) return;
        previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        surface.hidden = false;
        stack.push(panel);
        update();
        close.focus({ preventScroll: true });
      },
      close: () => {
        if (surface.hidden) return;
        surface.hidden = true;
        stack.splice(stack.indexOf(panel), 1);
        update();
        if (previous?.isConnected && !previous.closest("[hidden], [inert]"))
          previous.focus({ preventScroll: true });
        else (stack.at(-1)?.element ?? focusable(workspace)[0])?.focus({ preventScroll: true });
        panel.onClose?.();
      },
    };
    if (interactive) interactiveSurfaces.set(panel, interactiveSurface);
    close.onclick = panel.close;
    return panel;
  };
}
